import { useMemo, useRef, useState } from "react";
import { Icon, type IconName } from "@/components/common/Icon";
import { useChatStore } from "@/stores/useChatStore";
import { useOpenChat } from "@/components/chat/useOpenChat";
import { ACTION_LINK_CLASS } from "@/lib/link-styles";
import { EnvironmentChatRow } from "./EnvironmentChatRow";
import { EnvironmentChatsPopover } from "./EnvironmentChatsPopover";

const MAX_VISIBLE_CHATS = 5;

/**
 * チャットが無いときの案内。`icon` があればカード右上のそのボタンを指し、無ければ文言だけを出す。
 */
export interface EnvironmentChatsEmptyHint {
	icon?: IconName;
	text: string;
}

interface EnvironmentChatListProps {
	environmentId: string;
	emptyHint: EnvironmentChatsEmptyHint;
}

/**
 * エージェントのチャットを更新が新しい順に並べる。
 * カードが伸び続けないよう件数を絞り、残りは件数だけ出す。
 */
export function EnvironmentChatList({ environmentId, emptyHint }: EnvironmentChatListProps) {
	const openChat = useOpenChat();
	const chatList = useChatStore((s) => s.chatList);

	const chats = useMemo(
		() =>
			chatList
				.filter((chat) => chat.environmentId === environmentId && !chat.archived)
				.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
		[chatList, environmentId],
	);
	const visibleChats = chats.slice(0, MAX_VISIBLE_CHATS);
	const hiddenCount = chats.length - visibleChats.length;
	const [allChatsOpen, setAllChatsOpen] = useState(false);
	const moreButtonRef = useRef<HTMLButtonElement>(null);

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			{chats.length === 0 ? (
				<div
					data-testid={`environment-chats-empty-${environmentId}`}
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						gap: 8,
						padding: "20px 12px",
						borderRadius: "var(--radius-3)",
						boxShadow: "var(--shadow-ring)",
						textAlign: "center",
						color: "var(--fg-3)",
						fontSize: "var(--text-caption)",
					}}
				>
					<Icon name="chat" size={24} />
					<p style={{ margin: 0, color: "var(--fg-1)", fontWeight: 500 }}>
						チャットはまだありません
					</p>
					<EmptyHint hint={emptyHint} />
				</div>
			) : (
				<div
					data-testid={`environment-chats-${environmentId}`}
					style={{
						display: "flex",
						flexDirection: "column",
						borderRadius: "var(--radius-3)",
						boxShadow: "var(--shadow-ring)",
						overflow: "hidden",
					}}
				>
					{visibleChats.map((chat, index) => (
						<EnvironmentChatRow
							key={chat.id}
							chat={chat}
							first={index === 0}
							onSelect={() => void openChat(chat.id)}
						/>
					))}
				</div>
			)}
			{hiddenCount > 0 && (
				<div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 4 }}>
					<button
						ref={moreButtonRef}
						type="button"
						data-testid={`environment-chats-more-${environmentId}`}
						onClick={() => setAllChatsOpen((v) => !v)}
						className={ACTION_LINK_CLASS}
					>
						ほか {hiddenCount} 件のチャット
					</button>
					{allChatsOpen && (
						<EnvironmentChatsPopover
							chats={chats}
							anchorRef={moreButtonRef}
							onSelect={(chatId) => {
								setAllChatsOpen(false);
								void openChat(chatId);
							}}
							onClose={() => setAllChatsOpen(false)}
						/>
					)}
				</div>
			)}
		</div>
	);
}

function EmptyHint({ hint }: { hint: EnvironmentChatsEmptyHint }) {
	if (!hint.icon) return <p style={{ margin: 0 }}>{hint.text}</p>;
	return (
		<p style={{ margin: 0, display: "inline-flex", alignItems: "center", gap: 4 }}>
			カード右上の
			<Icon name={hint.icon} size={12} />
			{hint.text}
		</p>
	);
}
